import { ReactElement } from 'react'
import { Check, Error, Replay, Save, Warning } from '@mui/icons-material'
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'

interface Props {
  type?: 'saving' | 'unsaved' | 'error' | 'loading'
  onResetError?: () => void
  onSave?: () => void
  onLoad?: () => void
}

const SavingIndicator = ({ type, onResetError, onSave }: Props): ReactElement | null => {
  if (type === 'saving') {
    return (
      <Box
        sx={theme => ({
          backgroundColor: theme.palette.grey[100],
          padding: theme.spacing(0.75),
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        <Typography variant="caption">Wird gespeichert</Typography>
        <CircularProgress size={14} />
      </Box>
    )
  }

  if (type === 'loading') {
    return (
      <Box
        sx={theme => ({
          backgroundColor: theme.palette.grey[100],
          padding: theme.spacing(0.75),
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        <Typography variant="caption">Wird geladen</Typography>
        <CircularProgress size={14} />
      </Box>
    )
  }

  if (type === 'unsaved') {
    return (
      <Box
        sx={theme => ({
          backgroundColor: theme.palette.grey[100],
          padding: theme.spacing(0.75),
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        <Typography variant="caption">Ungespeicherte Änderungen</Typography>
        <Warning fontSize="small" color="warning" />
        <Tooltip title="Jetzt speichern">
          <IconButton
            onClick={() => {
              onSave?.()
            }}
            size="small"
            sx={{ padding: 0 }}
          >
            <Save fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }

  if (type === 'error') {
    return (
      <Box
        sx={theme => ({
          backgroundColor: theme.palette.grey[100],
          padding: theme.spacing(0.75),
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        <Typography variant="caption">Ungespeicherte Änderungen</Typography>
        <Error fontSize="small" color="error" />
        {onResetError && (
          <Tooltip title="Aktuellen Serverzustand laden (Ungespeicherte Änderungen gehen verloren)">
            <IconButton
              onClick={() => {
                onResetError?.()
              }}
              size="small"
              sx={{ padding: 0 }}
            >
              <Replay fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    )
  }

  return (
    <Box
      sx={theme => ({
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(0.75),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      })}
    >
      <Typography variant="caption">Synchronisiert</Typography>
      <Check fontSize="small" color="success" />
    </Box>
  )
}

export default SavingIndicator
